import React from 'react';
import { Compass, User, LayoutDashboard, ChevronRight } from 'lucide-react';
import { ChatMessage } from '../types';
import { TextBlock } from './TextBlock';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { hasResult } from '../workspace/resultUtils';

interface MessageBubbleProps {
    message: ChatMessage;
    /** Resultatet til denne meldingen vises i resultatkolonnen nå */
    isSelected?: boolean;
    /** Vis resultatet til denne meldingen i resultatkolonnen */
    onSelectResult?: (messageId: string) => void;
}

/**
 * En melding i samtalekolonnen. Svarets tekst står her; selve resultatet (visning, tabeller, grafer)
 * vises i resultatkolonnen, og knappen under teksten velger hvilket resultat som vises der.
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isSelected, onSelectResult }) => {
    const { t } = useTranslation();
    const time = message.timestamp.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });

    if (message.role === 'user') {
        return (
            <div className="flex justify-end">
                <div className="max-w-[85%]">
                    <div className="bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5">
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-400">{time}</span>
                    </div>
                </div>
                <div className="ml-2 mt-1 flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                        <User size={14} className="text-blue-600" />
                    </div>
                </div>
            </div>
        );
    }

    const result = hasResult(message);
    const errorText = message.blocks?.find(b => b.type === 'error')?.content;
    const resultTitle = message.view?.title
        || t('manager.modules.claudeQuery.workspace.resultFallbackTitle', {}, 'Resultat');
    const panelCount = message.view?.panels.length;

    return (
        <div className="flex justify-start">
            <div className="mr-2 mt-1 flex-shrink-0">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Compass size={14} className="text-white" />
                </div>
            </div>
            <div className="flex-1 min-w-0 max-w-[92%]">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                    {message.content && <TextBlock content={message.content} />}

                    {errorText && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{errorText}</div>
                    )}
                    {message.isError && !message.content && !errorText && (
                        <div className="text-sm text-red-600">
                            {t('manager.modules.claudeQuery.messageBubble.genericError', {}, 'Det oppstod en feil')}
                        </div>
                    )}

                    {result && onSelectResult && (
                        <button
                            onClick={() => onSelectResult(message.id)}
                            aria-pressed={isSelected}
                            className={`mt-2 w-full flex items-center gap-2 min-h-[44px] px-3 rounded-lg border text-left transition-colors ${isSelected
                                ? 'border-blue-200 bg-blue-50 text-blue-700'
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        >
                            <LayoutDashboard size={16} className="flex-shrink-0" />
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm font-medium truncate">{resultTitle}</span>
                                <span className="block text-xs text-gray-500">
                                    {isSelected
                                        ? t('manager.modules.claudeQuery.workspace.shownInResult', {}, 'Vises i resultatet')
                                        : panelCount
                                            ? t('manager.modules.claudeQuery.workspace.panelCount', { '0': panelCount }, `${panelCount} deler — trykk for å vise`)
                                            : t('manager.modules.claudeQuery.workspace.tapToShow', {}, 'Trykk for å vise')}
                                </span>
                            </span>
                            {!isSelected && <ChevronRight size={16} className="flex-shrink-0 text-gray-400" />}
                        </button>
                    )}
                </div>
                <div className="mt-1 px-1">
                    <span className="text-xs text-gray-400">{time}</span>
                </div>
            </div>
        </div>
    );
};
